import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import type { CaseId, CaseTransitionResult, ResolutionAction } from '../_shared/types';
import { caseKey } from './queries';

/**
 * **Two acts, two endpoints, two forms.** The owning service splits them
 * deliberately: a reply appends a message and moves nothing, and a transition
 * moves the state and writes no words. Collapsing them into one submit would
 * make «close it without saying anything» impossible and would send a
 * notification on a bookkeeping act.
 *
 * Neither auto-retries and neither is optimistic. Both are cheap to repeat and
 * neither is destructive, but a thread that shows a message the server has not
 * accepted is a lie the operator cannot see through — and the state badge is the
 * single source this page's own controls read.
 */

/**
 * The reply.
 *
 * It bumps `last_activity_at`, which is the queue's sort key, so the queue is
 * invalidated alongside the case — a reply that left the queue's order stale
 * would put the case an operator just answered back where it was.
 *
 * The owning service emits the «case replied» notification from here and from
 * nowhere else, which is exactly what the composer's hint promises.
 */
export function useReplyToCase(caseId: CaseId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: string) => api.post<null>('support', `/admin/cases/${caseId}/messages`, { body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: caseKey(caseId) });
      void queryClient.invalidateQueries({ queryKey: key('support', 'cases') });
    },
    retry: false,
  });
}

/**
 * The state control — **this surface's reason for existing.**
 *
 * The four states are the operator's work, and the subscriber pack says so
 * outright: no row lets a subscriber's reply move a case, and the badge is left
 * to the operator's surface. The report entry deliberately does not carry this
 * control either; a second control writing the same value from a second page is
 * how two surfaces come to disagree about one row.
 *
 * **`caseState` is a bounded string on the wire, never an enum** — the
 * vocabulary is a configuration value read at request time, so there is no enum
 * to type it as. An unknown member comes back as `422
 * SUPPORT_CASE_STATE_UNKNOWN` with the live vocabulary in `data.allowed`, rather
 * than as a generic validation failure nothing can map to a sentence.
 *
 * **No confirmation dialog.** A confirmation guards a destructive act; a state
 * move is reversible with the same control, and spending a modal on it would
 * cheapen the one that guards a real deletion.
 *
 * Transitioning to the state already in force is a no-op success server-side and
 * does not re-stamp the terminal timestamp.
 */
export function useTransitionCase(caseId: CaseId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseState: string) =>
      api.patch<CaseTransitionResult>('support', `/admin/cases/${caseId}`, { caseState }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: caseKey(caseId) });
      void queryClient.invalidateQueries({ queryKey: key('support', 'cases') });
      // A case leaving or entering an open state moves the console entry's
      // engagement band, which reports the open-case depth.
      void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'engagement') });
    },
    retry: false,
  });
}

export interface ResolveInput {
  resolutionAction: ResolutionAction;
  /** `admin` only, and never paired with a dismissal — the server refuses both. */
  blockOwner: boolean;
  /** The optional reply that explains the decision. */
  body?: string;
}

/**
 * The resolution — the third act, and the only one that reaches outside support.
 *
 * It moved here with the decision it commits. The endpoint was always a **case**
 * route, and the surface that offered it was a second page over the same record.
 *
 * **It does not auto-retry.** A resolution reaches outside this service and, on
 * this platform, part of it is irreversible: `DELETE_SUBJECT` destroys the record
 * and its stored renditions through `backend-content` and `backend-assets`. An
 * automatic second attempt against a title the first call already destroyed is
 * exactly the replay the server's `UNIQUE (case_id)` exists to refuse — the
 * client must not be the thing that generates it.
 *
 * **`DELETE_SUBJECT` is never a default and never inferred.** The action is an
 * explicit member of a three-value set; there is no missing-field path that
 * reaches it, and no fourth value exists.
 *
 * **No `confirm=true`.** That flag is the wire half of a hard removal on the
 * content and assets routes; this call names its own destructive action in a
 * required field, and the server's unique constraint on the resolution is what
 * makes a retry safe. Sending a flag the contract does not declare would document
 * a guard that does not exist. The dialog still runs — it is the operator's guard.
 *
 * **No `Idempotency-Key`.** The header is scoped platform-wide to finance-bound
 * money-moving writes, and the console runs none.
 *
 * The invalidation set reaches past support: an unpublish or a delete moved a
 * title, a block moved an account, and both move figures on the console entry.
 */
export function useResolveCase(caseId: CaseId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ResolveInput) =>
      api.post<null>('support', `/admin/cases/${caseId}:resolve`, {
        resolutionAction: input.resolutionAction,
        blockOwner: input.blockOwner,
        ...(input.body ? { body: input.body } : {}),
      }),
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: caseKey(caseId) });
      void queryClient.invalidateQueries({ queryKey: key('support', 'cases') });
      void queryClient.invalidateQueries({ queryKey: key('content', 'adminItems') });
      void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'content') });
      void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'engagement') });

      if (!input.blockOwner) return;
      void queryClient.invalidateQueries({ queryKey: key('auth', 'adminUsers') });
      void queryClient.invalidateQueries({ queryKey: key('analytics', 'report', 'users') });
    },
    retry: false,
  });
}
